import classNames from 'classnames';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { TooltipContainerContext } from 'components/common-form/contexts/TooltipContainerContext';
import { InvalidCount } from 'components/common-form/hooks/invalid-fields/model';
import { useCurrentBlockInvalidFields } from 'components/common-form/hooks/invalid-fields/useCurrentBlockInvalidFields';
import { useCurrentBlockModifiedCount } from 'components/common-form/hooks/patched-fields/useCurrentBlockModifiedCount';
import { useBlockMetadata } from 'components/common-form/hooks/useBlockMetadata';
import { InfoTooltip } from 'components/shared/InfoTooltip';
import { useToggle } from 'hooks/useToggle';
import type { IntlKey } from 'locales';
import { FC, useMemo, useRef } from 'react';
import { Overlay, OverlayProps, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { camelCaseToWords } from 'utils/string';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';
import { CountLabel } from './components/CountLabel';
import { FieldCountBadges } from './components/FieldCountBadges';
import { ChildNode, FieldsList } from './FieldsList';

export const PopoverBlock: FC<ProductNodeBuilderProps> = ({
  tabId,
  level,
  workspaceType,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  node,
}) => {
  const { id, children } = node;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isBatchWorkspace = workspaceType === 'newBatch';
  const { isOn, toggle } = useToggle();

  const metadata = useBlockMetadata(id);
  const { formatMessage } = useIntl();
  const blockLabel = useMemo(
    () => formatMessage({ id: `form.${id}` as any, defaultMessage: camelCaseToWords(id) }),
    [formatMessage, id],
  );
  const invalidCount = useCurrentBlockInvalidFields(children);
  const modifiedCount = useCurrentBlockModifiedCount(children);

  const isNestedBlock = level > 1;
  const hasNestedBlock = children?.some(c => (c.children?.length ?? 0) > 0);

  const isCollatSettingsField = id === 'CollatSettings';

  const toggleButtonId = `toggle-button-block-${id}`;

  return (
    <div data-e2e={`popover-block-container-${id}`} className={classNames({ 'pt-24px': isCollatSettingsField })}>
      <button
        id={toggleButtonId}
        type="button"
        data-e2e="popover-block-toggle-button"
        className="btn dropdown-toggle btn-flat-secondary w-100"
        onClick={toggle}
        disabled={metadata?.isReadOnly}
        ref={buttonRef}
      >
        <span>
          <i className="icon icon-sm me-1">tune</i>
          {blockLabel}
        </span>
        {metadata?.type === 'DefaultWithCount' && <CountLabel count={metadata.count} labelId={metadata.countLabelId} />}
        <FieldCountBadges id={id} value={invalidCount} modifiedCount={modifiedCount} />
        {metadata?.type === 'Feature' && (
          <InfoTooltip id={`info-feature-${metadata.id}`} tooltipKey={metadata.tooltipKey as IntlKey} />
        )}
      </button>
      <PopoverBlockDetails
        target={buttonRef.current}
        close={toggle}
        open={isOn}
        batchWorkspaceVersion={batchWorkspaceVersion}
        hasNestedBlock={hasNestedBlock}
        invalidCount={invalidCount}
        isBatchWorkspace={isBatchWorkspace}
        isNestedBlock={isNestedBlock}
        level={level}
        metadata={metadata}
        mustForceAdvancedMode={mustForceAdvancedMode}
        quoteWorkspaceVersion={quoteWorkspaceVersion}
        node={node}
        tabId={tabId}
        workspaceType={workspaceType}
      />
    </div>
  );
};
PopoverBlock.displayName = 'PopoverBlock';

const StyledTooltip = styled(Tooltip)`
  & > .tooltip-inner {
    padding: 0px;
  }
`;

const StyledPopoverBody = styled.div<{ $isBatchWorkspace: boolean }>`
  min-width: 360px;
  max-width: ${({ $isBatchWorkspace }) => ($isBatchWorkspace ? '40rem' : '30rem')};
`;

type PopoverBlockDetailsProps = {
  node: ProductNodeBuilderProps['node'];
  tabId: string;
  level: number;
  workspaceType: ProductNodeBuilderProps['workspaceType'];
  quoteWorkspaceVersion: ProductNodeBuilderProps['quoteWorkspaceVersion'];
  batchWorkspaceVersion: ProductNodeBuilderProps['batchWorkspaceVersion'];
  mustForceAdvancedMode: boolean;
  isBatchWorkspace: boolean;
  isNestedBlock: boolean;
  hasNestedBlock?: boolean;
  metadata: ReturnType<typeof useBlockMetadata>;
  invalidCount: InvalidCount;
  close: () => void;
  open: boolean;
  target: OverlayProps['target'];
};

const PopoverBlockDetails: FC<PopoverBlockDetailsProps> = ({
  node,
  tabId,
  level,
  workspaceType,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  isBatchWorkspace,
  isNestedBlock,
  hasNestedBlock,
  open,
  close,
  target,
}) => (
  <Overlay show={open} target={target} containerPadding={0}>
    <StyledTooltip>
      <StyledPopoverBody $isBatchWorkspace={isBatchWorkspace} className="text-primary bg-lvl1 shadow p-1">
        <div
          data-e2e="popover-block-content"
          id={node.id}
          className={classNames('block', { 'w-100': isNestedBlock, 'block-has-nested': hasNestedBlock })}
        >
          <TooltipContainerContext.Provider value={node.id}>
            <div
              className={classNames('card bg-lvl1 h-100', {
                ['shadow-sm']: !isNestedBlock,
                ['card-bordered-0 my-0']: isNestedBlock,
              })}
            >
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={close}
                  data-e2e="popover-close-button"
                />
              </div>
              <div className="card-body py-1 ps-3 pe-3">
                <div className="card-text">
                  <FormOrientationContext.Provider value={node.form ?? 'horizontal'}>
                    <FieldsList
                      direction={node.direction ?? 'leftToRight'}
                      nodes={node.children}
                      blockWidth="full"
                      renderChild={(childNode, defaultWidth) => (
                        <ChildNode
                          key={childNode.id}
                          tabId={tabId}
                          node={childNode}
                          level={level}
                          defaultWidth={defaultWidth}
                          workspaceType={workspaceType}
                          quoteWorkspaceVersion={quoteWorkspaceVersion}
                          batchWorkspaceVersion={batchWorkspaceVersion}
                          mustForceAdvancedMode={mustForceAdvancedMode}
                        />
                      )}
                    />
                  </FormOrientationContext.Provider>
                </div>
              </div>
            </div>
          </TooltipContainerContext.Provider>
        </div>
      </StyledPopoverBody>
    </StyledTooltip>
  </Overlay>
);
